/**
 * LOGGING A PLATFORM LEAD. The two channels no wire can carry, in three fields.
 */
import { useState, type FormEvent } from 'react';
import type { Channel } from '@fk/shared';
import { ApiError, createEnquiry } from '../../api.ts';
import { Button, SelectField, TextField } from '../../components/Field.tsx';
import { ErrorNotice } from '../../components/Notice.tsx';
import { isDay } from '../../format.ts';
import { CHANNEL_LABELS } from '../../labels.ts';

/**
 * THE TWO CHANNELS NO WIRE CAN CARRY. A Thumbtack lead lands in the Thumbtack app and a Local
 * Services lead arrives as a call on the published number, so neither can reach ops as a signed
 * push. Both kill rules count LEADS and not conversations, which means the dead ones are half the
 * measurement: the denominator is a person typing one row per lead, and that is what the small
 * form on this screen is for. The server names a lead nobody named, so it is three fields.
 */
type PlatformChannel = Extract<Channel, 'thumbtack' | 'local_services'>;
const PLATFORM_CHANNELS: readonly PlatformChannel[] = ['thumbtack', 'local_services'];

/**
 * LOG A PLATFORM LEAD. Twenty seconds a lead, and that is the whole design.
 *
 * "10 Local Services leads with zero bookings pauses it" and "15 Thumbtack leads with zero
 * bookings ends it" are rules about LEADS, so the leads that never replied are half of what they
 * count. Nothing carries them: a Thumbtack lead lands in the Thumbtack app, and a Local Services
 * lead arrives as a call on the published number. So a person types the platform and the day, and
 * the server names a lead nobody named rather than asking somebody to invent one.
 *
 * THE FORM STAYS OPEN after each save, because these are typed in a handful at a time off a phone
 * held in the other hand, and a form that closes after every row is a form nobody uses twice.
 */
export function LogAPlatformLead({
  today,
  onSaved,
  onCancel,
}: {
  today: string;
  onSaved: (sentence: string) => void;
  onCancel: () => void;
}) {
  const [channel, setChannel] = useState<PlatformChannel | ''>('');
  const [receivedOn, setReceivedOn] = useState(today);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!channel) {
      setProblem('Say which platform this lead came from.');
      return;
    }
    if (!isDay(receivedOn)) {
      setProblem('The platform lead date is not a real date.');
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      await createEnquiry({
        sourceChannel: channel,
        receivedOn,
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      setName('');
      setNotes('');
      onSaved('Logged. It counts whether or not they ever reply, which is what the rules read.');
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That lead was not saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <h2 className="font-serif text-lg">Log a platform lead</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        One row per lead, including the ones that never replied. Both platform rules count leads
        rather than conversations, so the dead ones are half the measurement.
      </p>

      {problem ? (
        <div className="mb-4">
          <ErrorNotice title="Nothing was saved" message={problem} />
        </div>
      ) : null}

      <div className="md:grid md:grid-cols-2 md:gap-4">
        <SelectField
          id="platform-lead-channel"
          label="Which platform"
          required
          value={channel}
          onChange={setChannel}
          options={PLATFORM_CHANNELS}
          toLabel={(option) => CHANNEL_LABELS[option]}
          placeholder="Choose one"
        />
        <TextField
          id="platform-lead-date"
          label="The platform's lead date"
          required
          type="date"
          value={receivedOn}
          onChange={setReceivedOn}
          hint={
            receivedOn > today
              ? 'That day has not happened yet, so it is recorded as today.'
              : 'The day it landed in the app, which is the day the reply clock starts.'
          }
        />
        <TextField
          id="platform-lead-name"
          label="Name, if they gave one"
          value={name}
          onChange={setName}
          hint="Left blank, it is filed under the platform's own name for the lead."
          autoComplete="off"
        />
        <TextField
          id="platform-lead-notes"
          label="Notes"
          value={notes}
          onChange={setNotes}
          hint="What they asked for, in their words, if there was anything."
          autoComplete="off"
        />
      </div>

      <div className="flex flex-col gap-3 md:flex-row">
        <Button type="submit" full disabled={saving}>
          {saving ? 'Saving' : 'Log this lead'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={saving}>
          Done
        </Button>
      </div>
    </form>
  );
}
